import StatusPill from '@/components/console/status-pill';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { formatMoney, formatNumber, formatPrice } from '@/lib/format';
import type { Plan } from '@/types/platform';
import type { ReactNode } from 'react';

export type BillingInterval = 'month' | 'year';

interface PlanCardProps {
  plan: Plan;
  interval: BillingInterval;
  current: boolean;
  // The button for this plan, when the person may change plans.
  action?: ReactNode;
}

const MB_PER_GB = 1024;

const SENDER_LINES: Record<Plan['features']['sender'], string> = {
  shared: 'Mail goes out from the CAP Rock Labs sender',
  from_name: 'Mail goes out under your name',
  domain: 'Mail goes out from your own domain',
};

function megabytes(value: number): string {
  if (value >= MB_PER_GB) return `${Number((value / MB_PER_GB).toFixed(1))} GB`;

  return `${formatNumber(value)} MB`;
}

function price(plan: Plan, interval: BillingInterval): string {
  if (!plan.paid) return 'Free';

  return interval === 'year'
    ? `${formatPrice(plan.yearlyPriceCents)} a year`
    : `${formatPrice(plan.monthlyPriceCents)} a month`;
}

// Every line comes from the plan itself. A limit that is not set is "not capped";
// a build credit limit that is not set gets no line at all.
function planLines(plan: Plan): string[] {
  const features = plan.features;
  const lines = [
    plan.appLimit === 1 ? '1 app' : `${formatNumber(plan.appLimit)} apps`,
    plan.databaseMb === null ? 'Records: not capped' : `${megabytes(plan.databaseMb)} of records`,
    `${formatNumber(plan.storageGb)} GB of attachments`,
    `${formatNumber(plan.emailLimit)} emails a month`,
  ];

  if (plan.buildCreditLimit !== null) {
    lines.push(`${formatNumber(plan.buildCreditLimit)} build credits a month`);
  }
  if (plan.storageOverageCentsPerGb !== null) {
    lines.push(`${formatMoney(plan.storageOverageCentsPerGb)} for each extra GB of attachments`);
  }
  if (plan.emailOverageCentsPerThousand !== null) {
    lines.push(`${formatMoney(plan.emailOverageCentsPerThousand)} for each extra 1,000 emails`);
  }

  lines.push(features.livePayments ? 'Live payments' : 'Payments in test mode only');
  lines.push(SENDER_LINES[features.sender]);
  lines.push(
    features.watermark
      ? 'Published apps show a CAP Rock Labs mark'
      : 'No CAP Rock Labs mark on published apps'
  );

  if (features.customDomain) lines.push('Your own web address');
  if (features.quickbooks) lines.push('QuickBooks connection');
  if (features.dedicatedDatabase) lines.push("Each app's records kept in their own store");
  if (features.developer === 'read') lines.push('Developer page, read only');
  if (features.developer === 'write') lines.push('Developer page, with writes');
  if (features.regionChoice) lines.push('Choose the region your records are kept in');
  if (features.gitExport) lines.push('Export your app');
  if (features.claimProject) lines.push('Claim the project and run it yourself');
  if (features.exceptionReview) lines.push('A person reviews drafts that could not be used');
  if (features.nativeBuildsPerQuarter > 0) {
    lines.push(
      features.nativeBuildsPerQuarter === 1
        ? '1 native app build a quarter'
        : `${formatNumber(features.nativeBuildsPerQuarter)} native app builds a quarter`
    );
  }

  return lines;
}

const PlanCard = ({ plan, interval, current, action }: PlanCardProps) => {
  return (
    <Card className={current ? 'border-foreground/40' : undefined}>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <h3 className="text-base">{plan.name}</h3>
          {current ? <StatusPill tone="info">Current plan</StatusPill> : null}
        </CardTitle>
        <p className="text-2xl font-semibold tracking-tight">{price(plan, interval)}</p>
      </CardHeader>

      <CardContent className="flex-1">
        <ul className="marker:text-muted-foreground list-disc space-y-1.5 pl-4 text-sm">
          {planLines(plan).map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </CardContent>

      {action ? <CardFooter>{action}</CardFooter> : null}
    </Card>
  );
};

export default PlanCard;
